import React, { useState, useMemo } from 'react';
import { User, UserCheck, UserX, ChevronRight, X, Search, Check } from 'lucide-react';
import ModalShell from './ModalShell';
import useModalBackButton from '../hooks/useModalBackButton';
import { haptic } from '../utils/vibration';

/**
 * Получение инициалов из имени пользователя (например, "Иван Иванов" -> "ИИ")
 */
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

/**
 * AssigneePicker - Компактная кнопка с модальным окном для выбора исполнителя
 */
const AssigneePicker = ({
  value,
  onChange,
  users = [],
  loading = false,
  disabled = false,
  label = 'Исполнитель',
  placeholder = 'Не назначен',
  disabledPlaceholder = '',
  modalTitle = '',
  helperText = '',
  fallbackName = '',
  buttonClassName = '',
  allowClear = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useModalBackButton(isOpen, () => setIsOpen(false));

  const selectedUser = useMemo(() => {
    if (!value) return null;
    return users.find((u) => String(u.id) === String(value)) || null;
  }, [users, value]);

  const displayName = useMemo(() => {
    if (selectedUser) {
      return selectedUser.name || selectedUser.login;
    }
    if (value && fallbackName) {
      return fallbackName;
    }
    return '';
  }, [selectedUser, value, fallbackName]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase().trim();
    return users.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const login = (u.login || '').toLowerCase();
      return name.includes(q) || login.includes(q);
    });
  }, [users, searchQuery]);

  const handleOpen = () => {
    if (disabled) return;
    haptic('open');
    setSearchQuery('');
    setIsOpen(true);
  };

  const handleClose = () => {
    haptic('close');
    setIsOpen(false);
  };

  const handleSelect = (userId) => {
    haptic('selection');
    onChange(userId ? String(userId) : '');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    haptic('selection');
    onChange('');
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={`group relative flex w-full items-center justify-between rounded-xl border p-2.5 text-left transition-all ${
          disabled
            ? 'border-gray-200 bg-gray-50/70 opacity-60 cursor-not-allowed dark:border-gray-800 dark:bg-gray-800/20'
            : selectedUser || displayName
              ? 'border-blue-200 bg-blue-50/40 hover:bg-blue-50/80 hover:border-blue-300 dark:border-blue-900/50 dark:bg-blue-950/20 dark:hover:bg-blue-950/30'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
        } ${buttonClassName}`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${
              selectedUser || displayName
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
            }`}
          >
            {selectedUser || displayName ? (
              getInitials(displayName)
            ) : (
              <User className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {label}
            </div>
            <div
              className={`truncate text-xs font-medium sm:text-sm ${
                selectedUser || displayName
                  ? 'text-gray-900 dark:text-gray-100 font-semibold'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {loading
                ? 'Загрузка сотрудников...'
                : disabled && disabledPlaceholder
                  ? disabledPlaceholder
                  : displayName || placeholder}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 pl-2">
          {(selectedUser || displayName) && allowClear && !disabled && (
            <span
              role="button"
              tabIndex={0}
              title="Снять назначение"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClear(e);
                }
              }}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-200/60 hover:text-red-600 dark:hover:bg-gray-800 dark:hover:text-red-400 transition-colors"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          {!disabled && (
            <div className="flex items-center text-xs font-medium text-blue-600 dark:text-blue-400">
              <span className="hidden sm:inline mr-1">
                {selectedUser || displayName ? 'Изменить' : 'Выбрать'}
              </span>
              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
            </div>
          )}
        </div>
      </button>

      {isOpen && (
        <ModalShell
          open={isOpen}
          onClose={handleClose}
          variant="window"
          panelClassName="w-full max-w-md p-4 max-h-[85vh] flex flex-col"
        >
          {/* Заголовок модального окна */}
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
                <UserCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {modalTitle || label}
                </h3>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {helperText || 'Выберите сотрудника или оставьте заказ без исполнителя'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Поиск */}
          {users.length > 4 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по имени или логину..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50/70 pl-9 pr-8 py-2 text-xs focus:border-blue-500 focus:bg-white focus:outline-none dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-100 dark:focus:bg-gray-900"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Список сотрудников */}
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-0.5 max-h-[50vh]">
            {/* Пункт: Не назначен */}
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={`w-full flex items-center justify-between rounded-xl border p-2.5 text-left transition-colors ${
                !value
                  ? 'border-blue-300 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-950/30'
                  : 'border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-400">
                  <UserX className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    Не назначен
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    Оставить без предварительного исполнителя
                  </div>
                </div>
              </div>
              <div
                className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
                  !value
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                {!value && <Check className="h-3 w-3 stroke-[3]" />}
              </div>
            </button>

            {/* Подзаголовок списка */}
            <div className="pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {`Доступные сотрудники (${filteredUsers.length})`}
            </div>

            {loading ? (
              <div className="rounded-xl bg-gray-50 py-6 text-center text-xs text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                Загрузка сотрудников...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="rounded-xl bg-gray-50 py-6 text-center text-xs text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                {searchQuery
                  ? 'Никого не найдено по запросу'
                  : 'Нет доступных исполнителей для этого этапа'}
              </div>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = String(user.id) === String(value);
                const userInitials = getInitials(user.name || user.login);

                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelect(user.id)}
                    className={`w-full flex items-center justify-between rounded-xl border p-2.5 text-left transition-colors ${
                      isSelected
                        ? 'border-blue-300 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-950/30'
                        : 'border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 text-xs font-semibold dark:bg-blue-900/40 dark:text-blue-300">
                        {userInitials}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {user.name || user.login}
                        </div>
                        {user.login && user.name && user.name !== user.login && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            @{user.login}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Нижняя кнопка */}
          <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={handleClose}
              className="w-full rounded-xl bg-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            >
              Закрыть
            </button>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default AssigneePicker;
